import { AfterViewInit, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { v4 as uuid } from 'uuid';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  Subscription,
  take,
} from 'rxjs';

import { Cabin, TranslatedBenefitRow } from '@fcom/dapi/api/models';
import { Amount } from '@fcom/dapi/interfaces';
import { unsubscribe } from '@fcom/core/index';
import { NotificationButtonStyle, TagTheme } from '@fcom/ui-components';
import { CabinClassSelectionFareFamily } from '@fcom/booking/modules/ticket-selection/interfaces';
import { effectiveCabinClass } from '@fcom/booking/modules/ticket-selection/utils/offer.utils';
import { ScrollService } from '@fcom/common/services';

import { DimensionsAndDetails, ExtendedFareFamily } from '../../../interfaces';

const SEATS_LEFT_LIMIT = 9;
type FareFamilyByCabin = Record<Cabin, ExtendedFareFamily[]>;

@Component({
  selector: 'fin-fare-family-list',
  styleUrls: ['./fare-family-list.component.scss'],
  templateUrl: './fare-family-list.component.html',
})
export class FlightSelectionFareFamilyListComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly Cabin = Cabin;
  readonly NotificationButtonStyle = NotificationButtonStyle;
  readonly SEATS_LEFT_LIMIT = SEATS_LEFT_LIMIT;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TagTheme = TagTheme;

  @Input({ required: true }) fareFamilies$: Observable<ExtendedFareFamily[]>;
  @Input({ required: true }) currencyCode: string;
  @Input() showUpsellCard = false;
  @Input() showKoreaNotification = false;
  @Input() roundedTop = false;
  @Input() allowAutoExpand = false;
  @Input()
  preSelectedFareFamilyId: string;
  @Input()
  isShortHaul$: Observable<boolean>;
  @Input()
  isLongHaulLegAndAy$: Observable<boolean> = EMPTY;
  @Input()
  isShowTotalPrice = true;
  @Input()
  enableFareFamilyAutoScroll$: Observable<boolean> = of(false);

  @Output() selectFareFamily = new EventEmitter<{
    fareFamily: ExtendedFareFamily;
    checked: boolean;
  }>();

  @Output() cabinIsExpanded = new EventEmitter<Cabin>();

  @Output() openCabinClassModal = new EventEmitter<string>();

  @Output() fareFamilyAutoScrolled = new EventEmitter<void>();

  private subscriptions: Subscription = new Subscription();

  fareFamiliesByCabin$: Observable<FareFamilyByCabin>;
  cabins$: Observable<Cabin[]>;
  cabinIsExpanded$ = new BehaviorSubject<Cabin>(null);
  minPricePerCabin$: Observable<
    Record<
      Cabin,
      {
        totalPrice: Amount;
        price: Amount;
        originalTotalPrice: Amount;
        originalPrice: Amount;
        quota: number;
        discountReasonCode?: string;
        isSpecialOffer?: boolean;
      }
    >
  >;
  selectedFareFamilyPerCabin$: Observable<Record<Cabin, ExtendedFareFamily>>;
  fareFamilyBenefits$: Observable<Record<string, TranslatedBenefitRow[]>>;
  uuid: string;
  isTicketTypeModalOpen = false;
  ticketTypeInfo$: Observable<DimensionsAndDetails>;
  cabinUpsellMap = {
    [Cabin.ECONOMY]: Cabin.ECOPREMIUM,
    [Cabin.ECOPREMIUM]: Cabin.BUSINESS,
    [Cabin.BUSINESS]: Cabin.FIRST,
  };
  defaultCabinOrder = [Cabin.ECONOMY, Cabin.ECOPREMIUM, Cabin.BUSINESS, Cabin.FIRST];
  upsellCardFareFamily$: Observable<CabinClassSelectionFareFamily>;

  // These are used for autoscroll for preselected fare family
  carouselReady$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  cabinExpandedAnimationEnded$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private scrollService: ScrollService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    this.uuid = uuid();
    this.fareFamiliesByCabin$ = this.fareFamilies$.pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      map((fareFamilies) => {
        return fareFamilies.reduce(
          (acc, fareFamily) => {
            const cabin = effectiveCabinClass(fareFamily) as Cabin;

            acc[cabin] = acc[cabin] || [];
            acc[cabin].push(fareFamily);

            return acc;
          },
          {} as Record<Cabin, ExtendedFareFamily[]>
        );
      })
    );

    this.cabins$ = this.fareFamilies$.pipe(
      map((fareFamilies) => {
        return fareFamilies.reduce((acc, fareFamily) => {
          const cabin = effectiveCabinClass(fareFamily) as Cabin;
          if (!acc.includes(cabin)) {
            acc.push(cabin);
          }

          return acc;
        }, []);
      }),
      map((cabinList) =>
        cabinList.sort((a, b) => this.defaultCabinOrder.indexOf(a) - this.defaultCabinOrder.indexOf(b))
      )
    );

    this.selectedFareFamilyPerCabin$ = this.fareFamilies$.pipe(
      map((fareFamilies) => {
        return fareFamilies.reduce(
          (acc, fareFamily) => {
            const cabinClass = effectiveCabinClass(fareFamily);

            acc[cabinClass] = acc[cabinClass] || null;

            if (fareFamily.selected) {
              acc[effectiveCabinClass(fareFamily)] = fareFamily;
            }

            return acc;
          },
          {} as Record<Cabin, ExtendedFareFamily>
        );
      })
    );

    this.subscriptions.add(
      this.cabins$.pipe(take(1)).subscribe((cabins) => {
        if (cabins.length === 1 && this.allowAutoExpand) {
          this.cabinIsExpanded$.next(cabins[0]);
        }
      })
    );

    this.minPricePerCabin$ = this.fareFamilies$.pipe(
      map((fareFamilies) => {
        return fareFamilies.reduce(
          (acc, fareFamily) => {
            const cabin = effectiveCabinClass(fareFamily);
            const currentLowestPrice = acc[cabin]?.totalPrice;

            if (!currentLowestPrice || Number(fareFamily.totalPrice) < Number(currentLowestPrice.amount)) {
              acc[cabin] = {
                totalPrice: {
                  amount: fareFamily.totalPrice,
                  currencyCode: this.currencyCode,
                },
                price: {
                  amount: fareFamily.price,
                  currencyCode: this.currencyCode,
                },
                isSpecialOffer: fareFamily?.isSpecialOffer,
                discountReasonCode: fareFamily?.discountReasonCode,
                quota: fareFamily.quota || 9,
                ...(fareFamily.originalPrice
                  ? {
                      originalPrice: {
                        amount: fareFamily.originalPrice,
                        currencyCode: this.currencyCode,
                      },
                    }
                  : {}),
                ...(fareFamily.originalTotalPrice
                  ? {
                      originalTotalPrice: {
                        amount: fareFamily.originalTotalPrice,
                        currencyCode: this.currencyCode,
                      },
                    }
                  : {}),
              };
            }

            return acc;
          },
          {} as Record<
            Cabin,
            {
              price: Amount;
              totalPrice: Amount;
              originalTotalPrice: Amount;
              originalPrice: Amount;
              quota: number;
            }
          >
        );
      })
    );

    this.ticketTypeInfo$ = combineLatest([this.fareFamilies$, this.cabinIsExpanded$]).pipe(
      map(([fareFamilies, expandedCabin]) => {
        if (!expandedCabin) {
          return null;
        }

        return {
          extendedFareFamilies: fareFamilies,
          cabinClass: expandedCabin,
          itineraries: [],
        };
      })
    );

    this.upsellCardFareFamily$ = combineLatest([this.cabinIsExpanded$, this.fareFamiliesByCabin$]).pipe(
      map(([currentCabin, fareFamiliesByCabin]) => {
        if (!this.showUpsellCard) {
          return null;
        }

        if (!currentCabin) {
          return null;
        }
        const nextCabinClass = this.findNextAvailableCabin(currentCabin, fareFamiliesByCabin);

        const nextCabinFareFamily: ExtendedFareFamily[] = fareFamiliesByCabin[nextCabinClass];

        if (!nextCabinFareFamily) {
          return null;
        }
        return {
          ...nextCabinFareFamily[0],
          cabinClass: nextCabinClass,
          preSelectFareFamily: nextCabinFareFamily[0],
        } as CabinClassSelectionFareFamily;
      })
    );

    this.subscriptions.add(
      this.cabinIsExpanded$.pipe(distinctUntilChanged()).subscribe((cabin) => {
        this.cabinIsExpanded.emit(cabin);
      })
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      combineLatest([
        this.enableFareFamilyAutoScroll$,
        this.carouselReady$,
        this.cabinExpandedAnimationEnded$,
        this.cabinIsExpanded$,
      ])
        .pipe(
          distinctUntilChanged(),
          filter(
            ([isFareFamilyAutoScrollEnabled, isReady, isEnded, isCabinExpanded]) =>
              isFareFamilyAutoScrollEnabled && isReady && isEnded && Boolean(isCabinExpanded)
          )
        )
        .subscribe(() => {
          const element = this.document.getElementById(this.preSelectedFareFamilyId);
          if (element) {
            this.scrollService.scrollToImmediate(element, 100, true, true);
            this.fareFamilyAutoScrolled.emit();
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  toggleCabin(cabin: Cabin): void {
    const nextValue = this.cabinIsExpanded$.getValue() ? null : cabin;
    this.cabinIsExpanded$.next(nextValue);
  }

  trackByFn(_i: number, item: ExtendedFareFamily): string {
    return item.fareFamilyCode;
  }

  openCabinAndSelect(fareFamily: CabinClassSelectionFareFamily): void {
    this.cabinIsExpanded$.next(fareFamily.cabinClass as Cabin);
  }

  private findNextAvailableCabin = (currentCabin: Cabin, fareFamiliesByCabin: FareFamilyByCabin) => {
    let nextCabin = this.cabinUpsellMap[currentCabin];
    // Loop to find the next available cabin by checking the keys in fareFamiliesByCabin
    while (nextCabin) {
      if (Object.keys(fareFamiliesByCabin).some((cabin) => cabin === nextCabin)) {
        return nextCabin;
      }
      nextCabin = this.cabinUpsellMap[nextCabin];
    }
    return null;
  };

  afterCarouselViewInit(): void {
    if (this.preSelectedFareFamilyId) {
      this.carouselReady$.next(true);
    }
  }

  cabinExpanded(): void {
    if (this.preSelectedFareFamilyId) {
      this.cabinExpandedAnimationEnded$.next(true);
    }
  }
}
